import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';

import { fData } from 'src/utils/format-number';

import {
  Autocomplete,
  Button,
  ButtonGroup,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import { useNavigate } from 'react-router';
import { useGetAllCities, useGetAllPaymentMethods } from 'src/api/account';
import {
  createNewRestaurant,
  updateRestaurantData,
  uploadRestaurantImage,
  useGetRestaurant,
} from 'src/api/restaurant';
import { scrollToFirstError } from 'src/auth/context/jwt/utils';
import { ConfirmDialog } from 'src/components/custom-dialog';
import FormProvider, { RHFSelect, RHFTextField, RHFUploadAvatar } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import CustomMultiSelect from 'src/components/custom-multiselect';
import { PHONE_REGEX_EXPRESSION } from 'src/config-global';
import { useBoolean } from 'src/hooks/use-boolean';
import { paths } from 'src/routes/paths';
import { ARABIC_REGEX, ENGLISH_REGEX } from 'src/utils/regex';
import { hasPermissions } from 'src/auth/guard/utils';
import { useAuthContext } from 'src/auth/hooks/use-auth-context';

const phoneRegExp = PHONE_REGEX_EXPRESSION;

// ----------------------------------------------------------------------

export default function RestaurantAccountGeneral({ id }: { id: string | undefined }) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuthContext();
  const { restaurant } = useGetRestaurant(Number(id));
  const { cities, citiesLoading, citiesError } = useGetAllCities();
  const { paymentMethods, paymentMethodsLoading, paymentMethodsError } = useGetAllPaymentMethods();
  const [imageFile, setImageFile] = useState<{ logo: any; cover: any }>({
    logo: null,
    cover: null,
  });
  const navigate = useNavigate();

  const cantUpdateMainGeneralCommission = hasPermissions(user, ['partners:update:restaurants:disabled:main_commission']);
  const cantUpdateLoyaltyGeneralCommission = hasPermissions(user, ['partners:update:restaurants:disabled:loyalty_commission']);
  const canHandleMultipleRestaurants = hasPermissions(user, ['partners:misc:handle_multiple_restaurants']);
  
  // Dynamic field permissions - check if user has the disabled permission
  const cantUpdateNameEn = hasPermissions(user, ['partners:update:restaurants:disabled:name_en' as any]);
  const cantUpdateNameAr = hasPermissions(user, ['partners:update:restaurants:disabled:name_ar' as any]);
  const cantUpdateOwnersContactNumber = hasPermissions(user, ['partners:update:restaurants:disabled:owners_contact_number' as any]);
  const cantUpdateSupplierCode = hasPermissions(user, ['partners:update:restaurants:disabled:supplier_code' as any]);
  const cantUpdateLogo = hasPermissions(user, ['partners:update:restaurants:disabled:logo' as any]);
  const cantUpdateCover = hasPermissions(user, ['partners:update:restaurants:disabled:cover' as any]);

  const [open, setOpen] = useState(false);
  const [deliveryTimeRanges, setDeliveryTimeRanges] = useState<
    {
      id: number | null;
      start: Date | null;
      end: Date | null;
    }[]
  >([{ id: null, start: null, end: null }]);

  const addTimeRange = () => {
    setDeliveryTimeRanges([...deliveryTimeRanges, { id: null, start: null, end: null }]);
  };

  // Function to update a time range
  const updateTimeRange = (index: number, field: 'start' | 'end', newValue: Date | null) => {
    const updatedRanges = [...deliveryTimeRanges];
    updatedRanges[index][field] = newValue;
    setDeliveryTimeRanges(updatedRanges);
  };

  // Function to remove a time range
  const removeTimeRange = (index: number) => {
    const updatedRanges = [...deliveryTimeRanges].filter((_, i) => i !== index);
    setDeliveryTimeRanges(updatedRanges);
  };

  // Base schema structure without required constraints
  const baseSchemaFields = {
    isEdittable: Yup.boolean(),
    name_en: Yup.string()
      .matches(ENGLISH_REGEX.pattern, ENGLISH_REGEX.message),
    name_ar: Yup.string()
      .matches(ARABIC_REGEX.pattern, ARABIC_REGEX.message),
    logo: Yup.mixed<any>().nullable(),
    cover: Yup.mixed<any>().nullable(),
    customer_service_whatsapp: Yup.string()
      .matches(phoneRegExp, 'Invalid phone number')
      .min(8, 'Phone number is too short')
      .max(8, 'Phone number is too long'),
    main_commission: Yup.string()
      .test('is-numeric', 'Commission must be a numeric value if provided', (value) => {
        if (!value) return true; // Allow empty/null/undefined
        const numValue = Number(value);
        return !Number.isNaN(numValue);
      }).nullable(),
    loyalty_commission: Yup.string()
      .test('is-numeric', 'Loyalty Commission must be a numeric value if provided', (value) => {
        if (!value) return true; // Allow empty/null/undefined
        const numValue = Number(value);
        return !Number.isNaN(numValue);
      }).nullable(),
    supplier_code: Yup.string().optional().nullable(),
    status: Yup.mixed().oneOf(['active', 'inactive']),
    owners_contact_number: Yup.string()
      .matches(phoneRegExp, 'Invalid phone number')
      .min(8, 'Phone number is too short')
      .max(8, 'Phone number is too long'),
    area: Yup.array().optional(),
    payment_methods: Yup.array().optional(),
  };

  // Schema for creating a new restaurant (stricter)
  const CreateRestaurantSchema = Yup.object().shape({
    ...baseSchemaFields,
    name_en: baseSchemaFields.name_en.required('Name is required'),
    name_ar: baseSchemaFields.name_ar.required('Arabic Name is required'),
    logo: baseSchemaFields.logo.required('Logo is required'),
    cover: baseSchemaFields.cover.required('Cover is required'),
    customer_service_whatsapp: baseSchemaFields.customer_service_whatsapp.required('Contact number is required'),
    // Commissions are always required on create
    main_commission: baseSchemaFields.main_commission.required('Commission is required'),
    loyalty_commission: baseSchemaFields.loyalty_commission.required('Loyalty Commission is required'),
    owners_contact_number: baseSchemaFields.owners_contact_number.required('Phone number is required'),
    area: baseSchemaFields.area.required('Area is required').min(1, 'Area is required'),
    payment_methods: baseSchemaFields.payment_methods.required('Payment Methods is required').min(1, 'Payment Methods is required'),
  });

  // Schema for updating an existing restaurant (more lenient due to PATCH)
  const UpdateRestaurantSchema = Yup.object().shape({
    ...baseSchemaFields,
    name_en: baseSchemaFields.name_en.optional(),
    name_ar: baseSchemaFields.name_ar.optional(),
    logo: baseSchemaFields.logo.optional(),
    cover: baseSchemaFields.cover.optional(),
    customer_service_whatsapp: baseSchemaFields.customer_service_whatsapp.optional(),
    main_commission: baseSchemaFields.main_commission.optional(),
    loyalty_commission: baseSchemaFields.loyalty_commission.optional(),
    owners_contact_number: baseSchemaFields.owners_contact_number.optional(),
    area: baseSchemaFields.area.optional(),
    payment_methods: baseSchemaFields.payment_methods.optional(),
  });

  const defaultValues = useMemo(
    () => ({
      isEdittable: !id,
      name_en: restaurant?.name_en || '',
      name_ar: restaurant?.name_ar || '',
      logo: restaurant?.logo || '',
      status: restaurant?.status || 'inactive',
      cover: restaurant?.cover || '',
      customer_service_whatsapp: restaurant?.customer_service_whatsapp || '',
      main_commission: restaurant?.main_commission || '',
      loyalty_commission: restaurant?.loyalty_commission || '',
      supplier_code: restaurant?.supplier_code?.toString() || '',
      owners_contact_number: restaurant?.owners_contact_number || '',
      area: [],
      payment_methods: [],
    }),
    [restaurant, id]
  );

  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(id ? UpdateRestaurantSchema : CreateRestaurantSchema),
    defaultValues,
  });

  const [tempSelectedAreas, setTempSelectedAreas] = useState<string[]>([]);

  useEffect(() => {
    if (restaurant?.areas) {
      setTempSelectedAreas(restaurant.areas.map((area) => area.id.toString()));
    }

    if (restaurant?.delivery_times) {
      const mappingRanges = restaurant.delivery_times.map((deliverytime) => {
        const startTime = moment(deliverytime.start_time, 'HH:mm:ss').toDate();
        const endTime = moment(deliverytime.end_time, 'HH:mm:ss').toDate();

        return { id: deliverytime.id, start: startTime, end: endTime };
      });
      setDeliveryTimeRanges(
        mappingRanges.length > 0
          ? mappingRanges.map((range) => ({
              id: range.id ?? null,
              start: range.start ?? null,
              end: range.end ?? null,
            }))
          : [{ id: null, start: null, end: null }]
      );
    }
  }, [restaurant]);

  const handleSave = () => {
    methods.setValue('area', tempSelectedAreas);
    setOpen(false);
  };

  const handleCancel = () => {
    setTempSelectedAreas((methods.watch('area') as string[]) || []); // Provide fallback []
    setOpen(false);
  };

  const confirm = useBoolean();

  const handleDeleteRestaurant = async () => {
    try {
      if (id) {
        await updateRestaurantData({id, is_deleted: true});
        enqueueSnackbar('Restaurant Deleted Successfully!');
        navigate(paths.dashboard.restaurants);
      }
    } catch (error) {
      if (typeof error.detail === 'string') {
        enqueueSnackbar(error.detail, { variant: 'error' });
      } else {
        enqueueSnackbar('Something went wrong!', { variant: 'error' });
      }
      console.error(error);
    } finally {
      confirm.onFalse();
    }
  };

  useEffect(() => {
    methods.reset({ ...defaultValues, isEdittable: true });

    const areas =
      restaurant?.areas?.map((area) => {
        if (typeof area === 'object') {
          return area.id.toString();
        }
        return area;
      }) || [];

    const payment_methods = restaurant?.payment_methods?.map((method) => method?.id) || [];
    methods.setValue('area', areas);
    methods.setValue('payment_methods', payment_methods);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues, methods]);

  const {
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    watch,
  } = methods;

  const values = watch();

  const onSubmit = handleSubmit(
    async (data) => {
      try {
        const filteredDeliveryTimeRanges: { id: number | null; start: Date; end: Date }[] =
          deliveryTimeRanges.filter((range) => range.start !== null && range.end !== null) as {
            id: number | null;
            start: Date;
            end: Date;
          }[];
        let isValid = true;
        filteredDeliveryTimeRanges.forEach((range) => {
          const startMinutes = range.start.getMinutes();
          const endMinutes = range.end.getMinutes();
          const startValid = startMinutes % 10 === 0;
          const endValid = endMinutes % 10 === 0;

          if (!startValid || !endValid) {
            isValid = false;
            enqueueSnackbar('Delivery times must be in 10 minute intervals', { variant: 'error' });
          }
        });

        if (!isValid) {
          return;
        }

        let ID = id || null;
        const isEdit = Boolean(ID);

        const formattedDeliveryTimes = deliveryTimeRanges
          .filter((range) => range.start !== null && range.end !== null)
          .map((range) => ({
            ...(range.id !== null && { id: range.id }),
            start_time: moment(range.start).format('HH:mm:ss'),
            end_time: moment(range.end).format('HH:mm:ss'),
          }));

        const upload_data: any = {
          ...(!cantUpdateNameEn && { name_en: data.name_en }),
          ...(!cantUpdateNameAr && { name_ar: data.name_ar }),
          customer_service_whatsapp: data.customer_service_whatsapp,
          ...(!cantUpdateMainGeneralCommission && { main_commission: data.main_commission }),
          ...(!cantUpdateLoyaltyGeneralCommission && { loyalty_commission: data.loyalty_commission }),
          status: data.status,
          ...(!cantUpdateSupplierCode && { supplier_code: data.supplier_code }),
          ...(!cantUpdateOwnersContactNumber && { owners_contact_number: data.owners_contact_number }),
          delivery_times: formattedDeliveryTimes,
          areas: data.area?.map((areaId) => ({ id: Number(areaId) })),
          payment_methods: data.payment_methods?.map((paymentMethodId) => ({
            id: Number(paymentMethodId),
          })),
        };

        if (isEdit) {
          upload_data.delivery_times =
            [...(restaurant?.delivery_times || []), ...formattedDeliveryTimes]?.map((e) => ({
              ...e,
              is_deleted: !formattedDeliveryTimes.find((dt) => dt.id == e.id),
            })) || [];

          upload_data.id = id;
          await updateRestaurantData(upload_data);
          enqueueSnackbar('Update success!');
        } else {
          const response = await createNewRestaurant(upload_data);
          ID = response.id.toString();
          enqueueSnackbar('Create success!');
        }

        if (ID && (imageFile.cover || imageFile.logo)) {
          await uploadRestaurantImage(ID, {
            cover: imageFile.cover,
            logo: imageFile.logo,
          });
        }

        if (ID) {
          navigate(paths.dashboard.restaurant.profile(ID));
        }
      } catch (error) {
        enqueueSnackbar(error.message || error.detail || 'Something went wrong', {
          variant: 'error',
        });
        console.error(error);
      }
    },
    (e) => {
      scrollToFirstError(e);
    }
  );

  const handleDropLogo = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });
      if (newFile) {
        setValue('logo', newFile, { shouldValidate: true });
        setImageFile({ ...imageFile, logo: newFile });
      }
    },
    [imageFile, setValue]
  );

  const handleDropCover = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });
      if (newFile) {
        setValue('cover', newFile, { shouldValidate: true });
        setImageFile({ ...imageFile, cover: newFile });
      }
    },
    [setValue, imageFile]
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container spacing={3}>
        <Grid xs={12} md={4}>
          <Card sx={{ pt: 10, pb: 5, px: 3, textAlign: 'center' }}>
            <RHFUploadAvatar
              name="logo"
              disabled={!values?.isEdittable || cantUpdateLogo}
              maxSize={5000000}
              onDrop={handleDropLogo}
              multiple={false}
              helperText={
                <Typography
                  variant="caption"
                  sx={{
                    mt: 3,
                    mb: 10,
                    mx: 'auto',
                    display: 'block',
                    textAlign: 'center',
                    color: 'text.disabled',
                  }}
                >
                  <strong>Logo</strong>
                  <br />
                  Allowed *.jpeg, *.jpg, *.png, *.gif
                  <br /> max size of {fData(5000000)}
                </Typography>
              }
            />

            <Divider />

            <RHFUploadAvatar
              sx={{ mt: 8 }}
              disabled={!values?.isEdittable || cantUpdateCover}
              name="cover"
              multiple={false}
              maxSize={5000000}
              onDrop={handleDropCover}
              helperText={
                <Typography
                  variant="caption"
                  sx={{
                    mt: 5,
                    mx: 'auto',
                    display: 'block',
                    textAlign: 'center',
                    color: 'text.disabled',
                  }}
                >
                  Cover Image
                </Typography>
              }
            />

            <RHFSelect
              disabled={!values?.isEdittable}
              name="status"
              label="Status"
              defaultValue={values?.status}
              sx={{ mt: 5 }}
            >
              <MenuItem key="active" value="active">
                Active
              </MenuItem>
              <MenuItem key="inactive" value="inactive">
                Inactive
              </MenuItem>
            </RHFSelect>
          </Card>
        </Grid>

        <Grid xs={12} md={8}>
          <Card sx={{ p: 3 }}>
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(1, 1fr)',
              }}
            >
              <RHFTextField
                name="owners_contact_number"
                label="Phone Number"
                disabled={!values?.isEdittable || cantUpdateOwnersContactNumber}
                sx={{ mt: 3 }}
                InputProps={{
                  autoComplete: 'off',
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="twemoji:flag-kuwait" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <RHFTextField 
                name="name_en" 
                label="Name" 
                disabled={!values?.isEdittable || cantUpdateNameEn} 
              />

              <RHFTextField 
                name="name_ar" 
                label="Name Ar" 
                disabled={!values?.isEdittable || cantUpdateNameAr} 
              />

              {!citiesLoading && !citiesError && cities && Array.isArray(cities) && (
                <Autocomplete
                  open={open}
                  onOpen={() => {
                    setOpen(true);
                  }}
                  onClose={() => {
                    setTempSelectedAreas((methods.watch('area') as string[]) || []);
                    setOpen(false);
                  }}
                  fullWidth
                  limitTags={3}
                  loadingText="Loading Area Name..."
                  multiple
                  options={cities?.map((city) => ({
                    id: city.id.toString(),
                    name: city.name_en,
                  }))}
                  disableCloseOnSelect
                  getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option?.name ?? ''
                  }
                  sx={{ mb: 1 }}
                  loading={citiesLoading}
                  renderOption={(props, option, { selected }) => (
                    <li {...props} key={option.id}>
                      <Checkbox
                        size="small"
                        disabled={!values?.isEdittable}
                        disableRipple
                        checked={selected}
                      />
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Area" placeholder="Select Area" />
                  )}
                  onChange={(_, value) => {
                    setTempSelectedAreas(value.map((v) => v.id));
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id.toString()}
                  value={cities
                    .filter((city) => tempSelectedAreas.includes(city?.id?.toString()))
                    .map((city) => ({
                      id: city.id.toString(),
                      name: city.name_en,
                    }))}
                  renderTags={(selected, getTagProps) => {
                    const numTags = selected.length;
                    const limitTags = 3;
                    return (
                      <>
                        {selected.slice(0, limitTags).map((option) => (
                          <Chip
                            sx={{ m: 0.2 }}
                            key={option.id}
                            label={option.name}
                            size="small"
                          />
                        ))}
                        {numTags > limitTags && ` +${numTags - limitTags}`}
                      </>
                    );
                  }}
                  PaperComponent={({ children }) => (
                    <Paper onMouseDown={(e) => e.preventDefault()}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          mb: 1,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          p: 1,
                        }}
                      >
                        <ButtonGroup size="small">
                          <Button
                            size="small"
                            onClick={() =>
                              setTempSelectedAreas(cities.map((city) => city.id.toString()))
                            }
                            disabled={!values?.isEdittable}
                          >
                            Select All
                          </Button>

                          <Button
                            color="primary"
                            onClick={handleSave}
                            disabled={!values?.isEdittable}
                          >
                            Save
                          </Button>
                          <Button
                            color="secondary"
                            onClick={handleCancel}
                            disabled={!values?.isEdittable}
                          >
                            Cancel
                          </Button>
                        </ButtonGroup>
                      </Box>
                      {children}
                    </Paper>
                  )}
                />
              )}
            </Box>

            <RHFTextField
              disabled={!values?.isEdittable || cantUpdateMainGeneralCommission}
              name="main_commission"
              label="Main Commission"
              sx={{ my: 2 }}
            />

            <RHFTextField
              disabled={!values?.isEdittable || cantUpdateLoyaltyGeneralCommission}
              name="loyalty_commission"
              label="Loyalty Commission"
              sx={{ my: 2 }}
            />

            <Divider />

            <RHFTextField
              disabled={!values?.isEdittable}
              name="customer_service_whatsapp"
              label="Contact Number"
              sx={{ mt: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="twemoji:flag-kuwait" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
            />

            <RHFTextField
              name="supplier_code"
              label="Supplier Code"
              sx={{ mt: 3, mb: 3 }}
              disabled={!values?.isEdittable || cantUpdateSupplierCode}
            />

            <Divider />

            <Box rowGap={3} sx={{ mt: 2, mb: 1 }}>
              <Typography variant="h6" mx={2}>
                Delivery Time
              </Typography>

              {deliveryTimeRanges.map((range, index) => (
                <Grid
                  container
                  rowSpacing={0.5}
                  key={index}
                  justifyContent="space-around"
                  alignItems="center"
                >
                  <Grid xs={4} md={4.5}>
                    <TimePicker
                      readOnly={false}
                      open={false}
                      disabled={!values?.isEdittable}
                      label="Start Time"
                      disableOpenPicker
                      slotProps={{ 
                        actionBar: { actions: [] },
                        textField: {
                          fullWidth: true,
                          margin: 'normal',
                        },
                      }}
                      value={range.start}
                      onChange={(newValue) => updateTimeRange(index, 'start', newValue)}
                      views={['hours', 'minutes']}
                      format="HH:mm"
                      timeSteps={{ minutes: 10 }}
                      ampm={false}
                      minutesStep={10}
                    />
                  </Grid>
                  <Grid xs={4} md={4.5}>
                    <TimePicker
                      readOnly={false}
                      open={false}
                      disabled={!values?.isEdittable}
                      label="End Time"
                      disableOpenPicker
                      value={range.end}
                      onChange={(newValue) => updateTimeRange(index, 'end', newValue)}
                      slotProps={{
                        actionBar: { actions: [] },
                        textField: {
                          fullWidth: true,
                          margin: 'normal',
                        },
                      }}
                      views={['hours', 'minutes']}
                      format="HH:mm"
                      timeSteps={{ minutes: 10 }}
                      ampm={false}
                      minutesStep={10}
                    />
                  </Grid>

                  <Grid xs={1} md={0.5}>
                    <IconButton
                      onClick={addTimeRange}
                      disabled={
                        !(index === deliveryTimeRanges.length - 1 && range.start && range.end) ||
                        !values?.isEdittable
                      }
                    >
                      <Iconify icon="basil:add-solid" />
                    </IconButton>
                  </Grid>

                  <Grid xs={1} md={0.5}>
                    <IconButton
                      onClick={() => removeTimeRange(index)}
                      disabled={deliveryTimeRanges.length === 1 || !values.isEdittable}
                    >
                      <Iconify icon="eva:trash-2-outline" />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </Box>

            <Box sx={{ mt: 3 }}>
              <CustomMultiSelect
                disabled={!values?.isEdittable || paymentMethodsLoading || !!paymentMethodsError}
                options={paymentMethods?.map((method) => method?.id) || []}
                value={values?.payment_methods || []}
                getOptionLabel={(option) => {
                  const method = paymentMethods?.find((m) => m?.id == option);
                  return method?.name || '';
                }}
                getTagLabel={(option) => {
                  const method = paymentMethods?.find((m) => m?.id == option);
                  return method?.name || '';
                }}
                onChange={(_, newValue) => {
                  methods.setValue('payment_methods', newValue);
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, idx) => {
                    const method = paymentMethods?.find((m) => m.id == option);
                    return (
                      <Chip
                        {...getTagProps({ index: idx })}
                        key={option}
                        label={method?.name}
                        disabled={!values?.isEdittable}
                      />
                    );
                  })
                }
                label="Payment Methods"
                placeholder="Select Payment Methods"
                error={Boolean(methods.formState.errors.payment_methods)}
                helperText={methods.formState.errors.payment_methods?.message}
              />
            </Box>

            {values?.isEdittable && (
              <Stack spacing={3} alignItems="flex-end" sx={{ mt: 3 }}>
                <div style={{ display: 'flex' }}>
                  {(Boolean(id) && canHandleMultipleRestaurants) && (
                    <Button
                      sx={{ mr: 1 }}
                      color="error"
                      onClick={() => confirm.onTrue()}
                      variant="contained"
                    >
                      Delete Restaurant
                    </Button>
                  )}

                  <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                    {id ? 'Update' : 'Create'}
                  </LoadingButton>
                </div>
              </Stack>
            )}
          </Card>
        </Grid>
      </Grid>
      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Delete"
        content="Are you sure want to delete?"
        action={
          <Button variant="contained" color="error" onClick={handleDeleteRestaurant}>
            Delete
          </Button>
        }
      />
    </FormProvider>
  );
}
